import { useMemo, useState } from 'react';
import {
  TripResponse,
  useGetProfilesQuery,
  usePatchTripsByTripIdMutation,
} from '../../store/backendApi';
import config from '../../config';

interface UseSharingOptionsReturn {
  // Form state
  shareMode: 'PUBLIC' | 'LINK' | 'PRIVATE' | null;
  slug: string;
  setShareMode: (mode: 'PUBLIC' | 'LINK' | 'PRIVATE') => void;
  setSlug: (slug: string) => void;

  // Computed values
  publicUrl: string | null;
  privateUrlBase: string;
  hasChanges: boolean;

  // Operations
  updateSharing: () => Promise<void>;
  reset: () => void;

  // Status
  patchTripLoading: boolean;

  // Profile data for URL generation
  profile: ReturnType<typeof useGetProfilesQuery>['data'];
}

/**
 * Hook to manage trip sharing options and URL configuration.
 * Platform-agnostic: Can be used in React Native.
 *
 * Handles:
 * - Share mode selection (PUBLIC, LINK, PRIVATE)
 * - Custom slug editing for public URLs
 * - URL generation for different share modes
 * - Change detection and form reset
 *
 * @param trip - The trip data to manage sharing for
 * @returns Sharing form state, handlers, and operations
 */
export const useSharingOptions = (trip: TripResponse): UseSharingOptionsReturn => {
  const { data: profile } = useGetProfilesQuery();
  const [shareMode, setShareMode] = useState<'PUBLIC' | 'LINK' | 'PRIVATE' | null>(trip.shareMode);
  const [slug, setSlug] = useState(trip.slug);
  const [patchTrip, { isLoading: patchTripLoading }] = usePatchTripsByTripIdMutation();

  // Computed values
  const publicUrl = useMemo(() => {
    return profile?.username ? `${config.media.host}/trip/${profile.username}/${slug}` : null;
  }, [profile?.username, slug]);

  const privateUrlBase = `${config.media.host}/trip`;

  const hasChanges = useMemo(() => {
    return slug !== trip.slug || shareMode !== trip.shareMode;
  }, [slug, trip.slug, shareMode, trip.shareMode]);

  // Operations
  const reset = () => {
    setSlug(trip.slug);
    setShareMode(trip.shareMode);
  };

  const updateSharing = async () => {
    await patchTrip({
      tripId: trip.id,
      partialTripBodyRequest: {
        slug: slug !== trip.slug ? slug : undefined,
        shareMode: shareMode !== trip.shareMode ? shareMode || undefined : undefined,
      },
    });
  };

  return {
    shareMode,
    slug,
    setShareMode,
    setSlug,
    publicUrl,
    privateUrlBase,
    hasChanges,
    updateSharing,
    reset,
    patchTripLoading,
    profile,
  };
};
